import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { Skeleton } from "@mui/material";

interface LazyAnalyticsWidgetProps {
  children: ReactNode;
  skeletonHeight?: number;
}

/**
 * Виджет монтируется (и, соответственно, запускает свой useQuery) только
 * после первого попадания в viewport — до этого рендерится Skeleton того же
 * размера. После первого монтирования остаётся смонтированным независимо от
 * дальнейшего скролла (не размонтируется обратно), чтобы не дублировать
 * сетевые запросы при скролле туда-обратно.
 */
export const LazyAnalyticsWidget: FC<LazyAnalyticsWidgetProps> = ({
  children,
  skeletonHeight = 350,
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (visible) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  if (visible) return <>{children}</>;

  return (
    <div ref={ref} style={{ width: "100%" }}>
      <Skeleton
        variant="rectangular"
        height={skeletonHeight}
        sx={{ width: "100%" }}
      />
    </div>
  );
};
